import React, { useState } from 'react';
import {
  ACTION_STATUS_LABELS,
  type ActionStatus,
  type EnterpriseAction,
} from '../../services/actionService';
import type { ActionCountermeasureRecord } from '../../services/actionCountermeasureService';
import type { IssueMaspEventRecord } from '../../services/issueMaspService';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { IssueEvidenceBoard } from './IssueEvidenceBoard';
import {
  executionHeadline,
  executionProgress,
  fiveW2HCompleteness,
  isActiveCountermeasure,
} from '../../utils/countermeasurePlan';

interface ExecutionWorkspaceProps {
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  extensions: ActionCountermeasureRecord[];
  events: IssueMaspEventRecord[];
  evidence: IssueEvidenceRecord[];
  closed: boolean;
  onOpenPlan: () => void;
  onChangeStatus: (action: EnterpriseAction, status: ActionStatus) => Promise<void>;
  onAddEvent: (input: { kind: 'training' | 'deviation'; title: string; body: string; occurredOn: string }) => Promise<void>;
  onAddEvidence: (file: File, caption: string) => Promise<void>;
  onDeleteEvidence: (record: IssueEvidenceRecord) => Promise<void>;
  actionsState?: 'loading' | 'error';
  eventsState?: 'error';
}

export const ExecutionWorkspace: React.FC<ExecutionWorkspaceProps> = ({
  actions,
  assigneeNames,
  extensions,
  events,
  evidence,
  closed,
  onOpenPlan,
  onChangeStatus,
  onAddEvent,
  onAddEvidence,
  onDeleteEvidence,
  actionsState,
  eventsState,
}) => {
  const direct = actions.filter((action) => action.problem_id);
  if (direct.length === 0) {
    const waiting = actionsState === 'loading' || actionsState === 'error';
    return (
      <div className="max-w-xl">
        <p className="text-[13px] font-medium text-slate-900">{waiting ? (actionsState === 'loading' ? 'Carregando contramedidas.' : 'Não foi possível carregar as contramedidas.') : 'Execução ainda não iniciada'}</p>
        {waiting ? null : <p className="mt-1 text-[13px] text-slate-600">Defina pelo menos uma contramedida no Plano de ação.</p>}
        <button type="button" onClick={onOpenPlan} className="mt-2 text-[12px] font-medium text-slate-700 hover:underline">
          Voltar para Plano de ação
        </button>
      </div>
    );
  }

  const progress = executionProgress(direct);
  const extensionOf = (actionId: string) => extensions.find((item) => item.actionId === actionId) ?? null;

  return (
    <div className="max-w-[960px] space-y-4">
      <section>
        <p className="text-[13px] text-slate-800">{executionHeadline(direct)}</p>
        <p className="mt-0.5 text-[12px] text-slate-500">
          Execução: {progress.completed} {progress.completed === 1 ? 'concluída' : 'concluídas'} / {progress.active} {progress.active === 1 ? 'não cancelada' : 'não canceladas'} · {progress.percent}%
        </p>
      </section>

      <ul className="border-t border-slate-200">
        {direct.map((action) => (
          <ExecutionRow
            key={action.id}
            action={action}
            assignee={action.assigned_to ? (assigneeNames[action.assigned_to] ?? 'Responsável') : '—'}
            extension={extensionOf(action.id)}
            closed={closed}
            onChangeStatus={onChangeStatus}
          />
        ))}
      </ul>

      <ExecutionEvents events={events} closed={closed} onAddEvent={onAddEvent} eventsState={eventsState} />
      <IssueEvidenceBoard
        records={evidence}
        editable={!closed}
        onAdd={onAddEvidence}
        onDelete={onDeleteEvidence}
        emptyLabel="Nenhuma evidência de execução."
      />
    </div>
  );
};

function ExecutionRow({
  action,
  assignee,
  extension,
  closed,
  onChangeStatus,
}: {
  action: EnterpriseAction;
  assignee: string;
  extension: ActionCountermeasureRecord | null;
  closed: boolean;
  onChangeStatus: (action: EnterpriseAction, status: ActionStatus) => Promise<void>;
}) {
  const [pending, setPending] = useState<ActionStatus | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cancelled = action.status === 'cancelled';
  const completeness = fiveW2HCompleteness({
    title: action.title,
    description: action.description,
    assignedTo: action.assigned_to,
    dueDate: action.due_date,
    whereText: extension?.whereText ?? null,
    howText: extension?.howText ?? null,
    costAmount: extension?.costAmount ?? null,
  });
  const apply = async (status: ActionStatus) => {
    setPending(status);
    try {
      await onChangeStatus(action, status);
    } finally {
      setPending(null);
      setConfirmCancel(false);
    }
  };
  const moves: Array<{ status: ActionStatus; label: string }> = [];
  if (!closed && isActiveCountermeasure(action.status) && action.status !== 'completed') {
    if (action.status !== 'in_progress') moves.push({ status: 'in_progress', label: action.status === 'blocked' ? 'Retomar' : 'Iniciar' });
    if (action.status === 'in_progress') moves.push({ status: 'completed', label: 'Concluir' });
    if (action.status !== 'blocked') moves.push({ status: 'blocked', label: 'Bloquear' });
  }

  return (
    <li className={`border-b border-slate-100 py-2 ${cancelled ? 'text-slate-400' : 'text-slate-800'}`}>
      <div className="flex min-w-0 items-baseline justify-between gap-3">
        <p className={`min-w-0 truncate text-[13px] ${cancelled ? 'line-through' : ''}`}>{action.title}</p>
        <span className="shrink-0 text-[12px]">{ACTION_STATUS_LABELS[action.status]}</span>
      </div>
      <p className="truncate text-[12px] text-slate-500">
        {assignee} · {formatDay(action.due_date)} · {completeness.label}
        {action.status === 'completed' && action.completed_at ? ` · concluída em ${formatDay(action.completed_at)}` : ''}
      </p>
      {moves.length > 0 || (!closed && !cancelled && action.status !== 'completed') ? (
        <div className="mt-1 flex flex-wrap gap-x-3 text-[12px]">
          {moves.map((move) => (
            <button
              key={move.status}
              type="button"
              disabled={pending != null}
              onClick={() => void apply(move.status)}
              className="font-medium text-slate-700 hover:underline disabled:opacity-50"
            >
              {pending === move.status ? 'Salvando...' : move.label}
            </button>
          ))}
          {!closed && !cancelled && action.status !== 'completed' ? (
            confirmCancel ? (
              <button type="button" disabled={pending != null} onClick={() => void apply('cancelled')} className="font-medium text-slate-600 hover:underline">
                Confirmar cancelamento
              </button>
            ) : (
              <button type="button" onClick={() => setConfirmCancel(true)} className="font-medium text-slate-500 hover:underline">
                Cancelar
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function ExecutionEvents({
  events,
  closed,
  onAddEvent,
  eventsState,
}: {
  events: IssueMaspEventRecord[];
  closed: boolean;
  onAddEvent: ExecutionWorkspaceProps['onAddEvent'];
  eventsState?: 'error';
}) {
  const [kind, setKind] = useState<'training' | 'deviation' | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!kind) return;
    setSaving(true);
    setError('');
    try {
      await onAddEvent({ kind, title, body, occurredOn });
      setKind(null);
      setTitle('');
      setBody('');
      setOccurredOn('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-baseline gap-x-4">
        <h3 className="text-[13px] font-semibold text-slate-900">Treinamentos e desvios</h3>
        {!closed && kind !== 'training' ? (
          <button type="button" onClick={() => { setKind('training'); setError(''); }} className="text-[12px] font-medium text-slate-700 hover:underline">
            + Registrar treinamento
          </button>
        ) : null}
        {!closed && kind !== 'deviation' ? (
          <button type="button" onClick={() => { setKind('deviation'); setError(''); }} className="text-[12px] font-medium text-slate-700 hover:underline">
            + Registrar desvio
          </button>
        ) : null}
      </div>
      {kind ? (
        <form onSubmit={(event) => void submit(event)} className="mt-2 max-w-md space-y-2">
          <label className="block text-[12px] text-slate-600">
            {kind === 'training' ? 'Treinamento' : 'Título'}
            <input
              aria-label={kind === 'training' ? 'Treinamento' : 'Título do desvio'}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-0.5 h-8 w-full rounded border border-slate-300 px-2 text-[13px]"
            />
          </label>
          <label className="block text-[12px] text-slate-600">
            {kind === 'deviation' ? 'Desvio' : 'Observação'}
            <textarea
              aria-label={kind === 'deviation' ? 'Desvio' : 'Observação do treinamento'}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={2}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-[13px]"
            />
          </label>
          <label className="block text-[12px] text-slate-600">
            Data
            <input
              aria-label="Data do evento"
              type="date"
              value={occurredOn}
              onChange={(event) => setOccurredOn(event.target.value)}
              className="mt-0.5 h-8 rounded border border-slate-300 px-2 text-[13px]"
            />
          </label>
          {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
          <div className="flex gap-3 text-[12px]">
            <button type="submit" disabled={saving} className="font-medium text-slate-800 hover:underline disabled:opacity-50">
              {saving ? 'Salvando...' : 'Registrar'}
            </button>
            <button type="button" onClick={() => setKind(null)} className="text-slate-500 hover:underline">Fechar</button>
          </div>
        </form>
      ) : null}
      {eventsState === 'error' && events.length === 0 ? <p className="mt-2 text-[13px] text-slate-600">Não foi possível carregar os eventos da execução.</p> : null}
      {events.length > 0 ? (
        <ul className="mt-2 border-t border-slate-200">
          {events.map((item) => (
            <li key={item.id} className="border-b border-slate-100 py-1.5 text-[12px]">
              <p className="text-slate-800">
                <span className="text-slate-500">{item.kind === 'training' ? 'Treinamento' : 'Desvio'}</span>
                {item.title ? ` · ${item.title}` : ''}
                {item.occurredOn ? ` · ${formatDay(item.occurredOn)}` : ''}
              </p>
              {item.body ? <p className="text-slate-600">{item.body}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function formatDay(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  if (!year || !month || !day) return '—';
  return `${day}/${month}/${year}`;
}
